import React, { HTMLAttributes, useEffect, useRef } from 'react';

import config from '@/appConfig';
import auth from '@/core/auth/AuthService';
import { camelToSnakeCase } from '@/utils/helpers';

type FormText = {
  loading?: string;
  cancel?: string;
  regionLabel?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  tosCheckboxLabelHtml?: string;
  tosCheckboxTitle?: string;
  gdprCheckboxLabel?: string;
  gdprCheckboxTitle?: string;
  rememberMeCheckboxLabel?: string;
  rememberMeCheckboxTitle?: string;
  submitSigninLabel?: string;
  submitCreateLabel?: string;
  emailInvalidError?: string;
  termsInvalidError?: string;
  notSignedInError?: string;
  genericError?: string;
  browserError?: string;
  permissionsError?: string;
  successHeading?: string;
  successMessage?: string;
}

const SOCIAL_FIRST_CLASSES = {
  container: 'shadow-none p-0 max-w-full',
  topBar: 'hidden',
  ssoButton: 'rounded py-3 px-4 justify-start border border-solid border-gray-300 shadow-none text-gray-700',
  textInput: 'rounded border border-solid border-gray-300 shadow-none text-gray-700 placeholder-gray-700',
  textLabel: 'sr-only',
  dividerBar: 'bg-gray-300',
  moreOptionsButton: 'lowercase text-gray-700',
};

type Props = HTMLAttributes<HTMLDivElement> & {
  onSignIn?: () => void;
  mode?: 'frameless' | 'normal' | 'social-first';
  heading?: string;
  subheading?: string;
  formText?: FormText;
  isAdminSignin?: boolean;
};
export default function UvAuth({onSignIn, mode, heading, subheading, formText, isAdminSignin, ...props}: Props) {
  const ref = useRef<JSX.IntrinsicElements['uv-auth']>(null);
  const isSocialFirst = mode === 'social-first';

  useEffect(() => {
    if (ref.current) {
      const handleLogin = () => {
        auth.checkForSession();
        onSignIn && onSignIn();
      };
      ref.current.loginSuccessHandler = handleLogin;
      ref.current.formText = formatFormText(formText || {});
      ref.current.classes = isSocialFirst ? SOCIAL_FIRST_CLASSES : null;

      if (isAdminSignin) {
        ref.current.subdomainSettingsFilter = { context: 'admin' };
      }
    } else {
      throw new Error('uv-auth did not initialize...');
    }
  }, []);

  const headingElements = [
    <h1 key="header">{heading || 'We\'re glad you\'re here'}</h1>,
    <p key="subheading">{subheading || 'Please sign in to leave feedback'}</p>,
  ];

  return (
    <div {...props}>
      <uv-auth
        mode={mode || 'normal'}
        magic-public-key={config().magic.apiPublicKey}
        ref={ref}
        signup-disabled={isAdminSignin}
      >
        {!isSocialFirst && headingElements}
      </uv-auth>
    </div>
  );
}

function formatFormText(formText: FormText) {
  const snakeCased: { [key: string]: string } = {};
  Object.keys(formText).forEach((camelKey) => {
    const value = formText[camelKey as keyof FormText];
    if (value) {
      snakeCased[camelToSnakeCase(camelKey)] = value;
    }
  });
  return snakeCased;
}
